import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export default function PopularDestinationList() {
  const navigate = useNavigate();

  const [rows, setRows]       = useState([]);
  const [meta, setMeta]       = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState("");

  const [search, setSearch]     = useState("");
  const [country, setCountry]   = useState("");
  const [iataCode, setIataCode] = useState("");
  const [page, setPage]         = useState(1);
  const [limit, setLimit]       = useState(10);

  const fetchList = async (opts = {}) => {
    const nextPage  = opts.page  ?? page;
    const nextLimit = opts.limit ?? limit;

    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/popular-destinations", {
        params: {
          search:    search    || undefined,
          country:   country   || undefined,
          iata_code: iataCode  || undefined,
          page:      nextPage,
          limit:     nextLimit,
        },
      });
      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load data";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList({ page: 1 }); }, []);

  const onApply = (e) => {
    e.preventDefault();
    setPage(1);
    fetchList({ page: 1 });
  };

  const onReset = () => {
    setSearch("");
    setCountry("");
    setIataCode("");
    setPage(1);
    setTimeout(() => fetchList({ page: 1 }), 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this destination?")) return;
    try {
      await api.delete(`/admin/popular-destinations/${id}`);
      toast.success("Deleted successfully");
      fetchList({ page });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    }
  };

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchList({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchList({ page: p });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Popular Destinations</h1>
        <button
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 self-start"
          onClick={() => navigate("/admin/popular-destinations/create")}
        >
          + Add Destination
        </button>
      </div>

      <form
        onSubmit={onApply}
        className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4"
      >
        <div className="min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Airport, city, country..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="min-w-[160px]">
          <label className="text-sm font-semibold">IATA Code</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black uppercase"
            placeholder="e.g. DEL"
            value={iataCode}
            onChange={(e) => setIataCode(e.target.value.toUpperCase())}
          />
        </div>

        <div className="min-w-[160px]">
          <label className="text-sm font-semibold">Country</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="e.g. India"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
        </div>

        <div className="min-w-[130px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={(e) => {
              const v = Number(e.target.value) || 10;
              setLimit(v);
              setPage(1);
              fetchList({ page: 1, limit: v });
            }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <button type="submit" className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90">
          Apply
        </button>
        <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50" onClick={onReset}>
          Reset
        </button>
      </form>

      {err && <div className="mt-4 text-sm text-red-600">{err}</div>}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>
            Destinations{" "}
            <span className="text-gray-500 font-normal">({meta.total} records)</span>
          </span>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>
            <button
              type="button"
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No records found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-t bg-white">
                <tr className="text-left">
                  <th className="px-4 py-3 whitespace-nowrap">Rank</th>
                  <th className="px-4 py-3 whitespace-nowrap">Airport</th>
                  <th className="px-4 py-3 whitespace-nowrap">IATA</th>
                  <th className="px-4 py-3 whitespace-nowrap">City / Region</th>
                  <th className="px-4 py-3 whitespace-nowrap">Country</th>
                  <th className="px-4 py-3 whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-bold text-gray-500">#{row.rank}</td>
                    <td className="px-4 py-3 font-semibold max-w-xs truncate">{row.airport}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold">
                        {row.iata_code}
                      </span>
                    </td>
                    <td className="px-4 py-3">{row.city_region}</td>
                    <td className="px-4 py-3">{row.country}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${row.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                        {row.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => navigate(`/admin/popular-destinations/edit/${row.id}`)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(row.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
